import './styles.scss'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { ROUTE_STRINGS } from 'Utils/Constants'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'

const EditVolumeAndInterest = ({ setLoading }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const volumeInterestData = location?.state?.volumeInterestData

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)
  const [inputValues, setInputValues] = useState({
    date: '',
    marketName: '',
    interestDifference: '',
    interest: '',
    volume: '',
    volumeDifference: ''
  })

  const [fieldValidity, setFieldValidity] = useState({
    date: false,
    marketName: false,
    interestDifference: false,
    interest: false,
    volume: false,
    volumeDifference: false
  })

  const [isFormEnabled, setIsFormEnabled] = useState({
    editMode: false,
    submitButton: false
  })

  const [modalState, setModalState] = useState({
    showModal: false,
    modalType: '',
    message: '',
    onHide: () => {},
    onRetry: () => {}
  })

  useEffect(() => {
    if (volumeInterestData) {
      const originalData = {
        id: volumeInterestData.id,
        marketId: volumeInterestData.marketId,
        date: volumeInterestData.date ? new Date(volumeInterestData.date) : null,
        marketName: volumeInterestData.marketName || '',
        interestDifference: volumeInterestData.openInterestDifference || '',
        interest: volumeInterestData.totalOpenInterest || '',
        volume: volumeInterestData.totalVolume || '',
        volumeDifference: volumeInterestData.volumeDifference || ''
      }

      setInputValues(originalData)
    }
  }, [volumeInterestData])

  const formatDateForApi = (date) => {
    if (!date) return ''

    const day = String(date.getDate()).padStart(2, '0')
    const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date?.getMonth()]
    const year = String(date.getFullYear()).slice(-2)

    return `${day}-${month}-${year}`
  }

  const updateVolumeAndInterest = async () => {
    setModalState({ ...modalState, showModal: false })
    setLoading(true)

    try {
      const payload = {
        id: inputValues?.id,
        date: formatDateForApi(inputValues?.date),
        marketId: inputValues?.marketId,
        totalVolume: inputValues?.volume,
        volumeDifference: inputValues?.volumeDifference,
        totalOpenInterest: inputValues?.interest,
        openInterestDifference: inputValues?.interestDifference
      }

      const response = await apiAdapterCoffeeWeb.updateVolumeInterestData(payload)

      if (response?.status === 200) {
        setModalState({
          showModal: true,
          modalType: 'success',
          message: t('VOLUME_INTEREST_UPDATED_SUCCESSFULLY'),
          onHide: () => handleNavigate(),
          onSuccess: () => handleNavigate()
        })
      }
    } catch (err) {
      setLoading(false)
      setModalState({
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_UPDATE_VOLUME_INTEREST'),
        onRetry: () => handleSubmit(),
        onHide: () => handleDismiss()
      })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setInputValues((prev) => ({
      ...prev,
      [field]: value
    }))
    setIsSubmitDisabled(false)
  }

  const handleBlur = (field) => {
    setFieldValidity((prev) => ({
      ...prev,
      [field]: true
    }))
  }

  const handleDismiss = () => {
    setModalState({ ...modalState, showModal: false })
  }

  const handleNavigate = () => {
    history.push({ pathname: ROUTE_STRINGS.volumnInterest })
  }

  const handleSubmit = () => {
    updateVolumeAndInterest()
  }

  const confirmResetForm = () => {
    setIsSubmitDisabled(true)
    setIsFormEnabled({
      editMode: false,
      submitButton: false
    })
    setModalState({ ...modalState, showModal: false })

    const originalData = {
      id: volumeInterestData.id,
      date: volumeInterestData.date ? new Date(volumeInterestData.date) : null,
      marketName: volumeInterestData.marketName || '',
      interestDifference: volumeInterestData.openInterestDifference || '',
      interest: volumeInterestData.totalOpenInterest || '',
      volume: volumeInterestData.totalVolume || '',
      volumeDifference: volumeInterestData.volumeDifference || ''
    }

    setInputValues(originalData)
  }

  const enableFormEditing = () => {
    setIsFormEnabled({
      editMode: true,
      submitButton: true
    })
  }

  const showResetConfirmationDialog = () => {
    setModalState({
      ...modalState,
      showModal: true,
      modalType: 'info',
      message: t('ARE_YOU_SURE_YOU_WANT_TO_PROCEED'),
      onConfirmation: confirmResetForm,
      onHide: handleDismiss
    })
  }

  const { interestDifference, interest, volume, volumeDifference } = inputValues

  const isDisable = () => {
    const fieldsToCheck = [interestDifference, interest, volume, volumeDifference]

    return fieldsToCheck?.some((field) => field === '' || field === null)
  }

  return (
    <LayoutContainer breadCrumbsTitle={'editVolumeAndInterest'}>
      <div className="volume-editor-container">
        <div className="header-wrapper">
          <div className="header-title">{t('EDIT_VOLUME_DATA')}</div>
          <div className="header-action-button">{isFormEnabled.editMode ? <ButtonComponent type={'reset'} onClick={showResetConfirmationDialog} /> : <ButtonComponent type={'edit'} onClick={enableFormEditing} />}</div>
        </div>
        <div className="edit-volume-interest-wrapper">
          <div className="edit-volume-row">
            <div className="edit-volume-field">
              <span className="p-float-label">
                <Calendar id="date" value={inputValues.date} className="text-input" showIcon dateFormat="dd/mm/yy" disabled readOnlyInput />
                <label htmlFor="date">
                  {t('DATE')} <span className="label-required">*</span>
                </label>
              </span>
            </div>

            <div className="edit-volume-field">
              <span className="p-float-label">
                <InputText id="marketName" value={inputValues.marketName} className="text-input" disabled readOnlyInput />
                <label htmlFor="marketName">
                  {t('MARKET_TYPE')} <span className="label-required">*</span>
                </label>
              </span>
            </div>
          </div>

          <div className="edit-volume-row">
            <div className={`edit-volume-field ${fieldValidity.volume && !inputValues.volume ? 'error' : ''}`}>
              <span className="p-float-label">
                <InputText id="volume" value={inputValues.volume} onChange={(e) => handleInputChange('volume', e.target.value)} onBlur={() => handleBlur('volume')} className={`text-input ${fieldValidity.volume && !inputValues.volume ? 'error' : ''}`} disabled={!isFormEnabled.editMode} />
                <label htmlFor="volume">
                  {t('VOLUME')} <span className="label-required">*</span>
                </label>
              </span>
              {fieldValidity.volume && !inputValues.volume && <small className="validation-error-message">{t('VOLUME_IS_REQUIRED')}</small>}
            </div>

            <div className={`edit-volume-field ${fieldValidity.volumeDifference && !inputValues.volumeDifference ? 'error' : ''}`}>
              <span className="p-float-label">
                <InputText id="volumeDifference" value={inputValues.volumeDifference} onChange={(e) => handleInputChange('volumeDifference', e.target.value)} onBlur={() => handleBlur('volumeDifference')} className={`text-input ${fieldValidity.volumeDifference && !inputValues.volumeDifference ? 'error' : ''}`} disabled={!isFormEnabled.editMode} />
                <label htmlFor="volumeDifference">
                  {t('VOLUME_DIFFERENCE')} <span className="label-required">*</span>
                </label>
              </span>
              {fieldValidity.volumeDifference && !inputValues.volumeDifference && <small className="validation-error-message">{t('VOLUME_DIFFERENCE_IS_REQUIRED')}</small>}
            </div>
          </div>

          <div className="edit-volume-row">
            <div className={`edit-volume-field ${fieldValidity.interest && !inputValues.interest ? 'error' : ''}`}>
              <span className="p-float-label">
                <InputText id="interest" value={inputValues.interest} onChange={(e) => handleInputChange('interest', e.target.value)} onBlur={() => handleBlur('interest')} className={`text-input ${fieldValidity.interest && !inputValues.interest ? 'error' : ''}`} disabled={!isFormEnabled.editMode} />
                <label htmlFor="interest">
                  {t('INTEREST')} <span className="label-required">*</span>
                </label>
              </span>
              {fieldValidity.interest && !inputValues.interest && <small className="validation-error-message">{t('INTEREST_IS_REQUIRED')}</small>}
            </div>

            <div className={`edit-volume-field ${fieldValidity.interestDifference && !inputValues.interestDifference ? 'error' : ''}`}>
              <span className="p-float-label">
                <InputText id="interestDifference" value={inputValues.interestDifference} onChange={(e) => handleInputChange('interestDifference', e.target.value)} onBlur={() => handleBlur('interestDifference')} className={`text-input ${fieldValidity.interestDifference && !inputValues.interestDifference ? 'error' : ''}`} disabled={!isFormEnabled.editMode} />
                <label htmlFor="interestDifference">
                  {t('INTEREST_DIFFERENCE')} <span className="label-required">*</span>
                </label>
              </span>
              {fieldValidity.interestDifference && !inputValues.interestDifference && <small className="validation-error-message">{t('INTEREST_DIFFERENCE_IS_REQUIRED')}</small>}
            </div>
          </div>

          {isFormEnabled.submitButton && (
            <div className="form-action-buttons">
              <ButtonComponent type="cancel" onClick={handleNavigate} />
              <ButtonComponent type="submit" label={t('SAVE')} onClick={handleSubmit} disabled={isSubmitDisabled || isDisable()} />
            </div>
          )}
        </div>
      </div>

      <DialogModal {...modalState} onDismiss={handleDismiss} />
    </LayoutContainer>
  )
}

export default Loader(EditVolumeAndInterest)
